export type BusinessCodeDomain =
  | 'auditAction'
  | 'auditCheck'
  | 'auditModule'
  | 'auditResult'
  | 'catalogStatus'
  | 'commissionRole'
  | 'consumptionStatus'
  | 'courseType'
  | 'employeeType'
  | 'employmentStatus'
  | 'exportStatus'
  | 'exportTaskType'
  | 'fileStatus'
  | 'notificationType'
  | 'purchaseLedgerOperation'
  | 'purchaseStatus'
  | 'reconciliationStatus'
  | 'studentGender'
  | 'studentStatus'
  | 'userRole';

export interface BusinessCodeDisplay {
  readonly color: string;
  readonly description: string;
  readonly known: boolean;
  readonly label: string;
  readonly technicalValue: string;
}

interface DictionaryEntry {
  readonly color?: string;
  readonly description: string;
  readonly label: string;
}

const dictionaries = {
  auditAction: {
    'auth.admin.initialize': entry('初始化管理员', '创建系统唯一管理员账号'),
    'auth.login': entry('登录', '账号成功登录系统'),
    'auth.login.failure': entry('登录失败', '登录请求未通过认证'),
    'auth.logout': entry('退出登录', '结束当前登录'),
    'auth.password.change': entry('修改密码', '员工修改自己的登录密码'),
    'consumption-batch.create': entry('创建课销批次', '创建单人或多人课销批次'),
    'consumption-batch.replace-voucher': entry('替换课销凭证', '替换课销批次的当前凭证'),
    'consumption-batch.update': entry('修改课销批次', '修改课销批次公共信息'),
    'consumption.cancel': entry('撤销课销', '撤销课销并返还对应课时'),
    'consumption.create': entry('录入课销', '创建学员课销记录'),
    'consumption.update': entry('修改课销', '修改单条课销并重新计算'),
    'consumption.update-batch-fields': entry('修改批次公共信息', '修改课销批次公共字段'),
    'employee.create': entry('创建员工', '创建员工档案和登录账号'),
    'employee.password.reset': entry('重置员工密码', '管理员重置员工登录密码'),
    'employee.terminate': entry('员工离职', '将员工标记为不可恢复的离职状态'),
    'employee.update': entry('修改员工', '修改员工档案或类型'),
    'export-task.create': entry('创建导出任务', '按当前筛选条件生成导出文件'),
    'export-task.download': entry('下载导出文件', '下载导出结果'),
    'file.view': entry('查看凭证', '查看凭证图片'),
    'formula.create': entry('创建分成公式', '创建教练分成公式'),
    'package-template.create': entry('创建课包', '创建课包模板'),
    'package-template.update': entry('修改课包', '修改课包配置或状态'),
    'purchase.create': entry('新增购课', '为学员创建购课记录'),
    'purchase.replace-voucher': entry('替换购课凭证', '替换购课记录的当前凭证'),
    'purchase.sales-attribution.update': entry(
      '更正销售业绩归属',
      '更正购课对应的前台销售员工或业绩时间',
    ),
    'purchase.update': entry('修改购课', '修改未课销购课记录或其状态'),
    'sales-commission-plan.save': entry('保存销售分成计划', '保存前台销售员工的分成计划版本'),
    'reconciliation.run': entry('运行业务对账', '执行只读业务一致性核对'),
    'student.create': entry('创建学员', '创建学员档案'),
    'student.mark-lost': entry('变更学员状态', '标记学员流失或恢复正常'),
    'student.restore-active': entry('恢复学员状态', '将流失学员恢复为正常状态'),
    'student.update': entry('修改学员', '修改学员档案'),
  },
  auditCheck: {
    ACTIVE_COMMISSION_ASSOCIATION: entry('分成关联', '核对有效课销与分成关联'),
    ACTIVE_CONSUMPTION_BALANCE: entry('有效课销扣减', '核对有效课销与余额扣减'),
    CURRENT_VOUCHER_CARDINALITY: entry('当前凭证数量', '核对业务记录的当前凭证数量'),
    CURRENT_VOUCHER_FILE_BINDING: entry('凭证文件绑定', '核对当前凭证与安全文件绑定'),
    EXPORT_TERMINAL_FILE: entry('导出终态文件', '核对成功导出任务与文件'),
    PURCHASE_BALANCE_LEDGER: entry('余额与流水', '核对购课余额与课时流水'),
  },
  auditModule: {
    AUTH: entry('认证与账号', '登录、退出和密码操作'),
    COMMISSION: entry('教练分成', '教练分成明细'),
    CONSUMPTION: entry('课销', '单条课销记录'),
    CONSUMPTION_BATCH: entry('课销批次', '课销批次公共信息'),
    EMPLOYEE: entry('员工', '员工档案与账号'),
    EXPORT_TASK: entry('导出任务', '导出文件生成任务'),
    FILE: entry('文件', '凭证上传与访问'),
    FORMULA: entry('分成公式', '教练分成公式'),
    NOTIFICATION: entry('通知', '站内任务通知'),
    PACKAGE_TEMPLATE: entry('课包模板', '课包模板配置'),
    PURCHASE: entry('购课', '学员购课记录'),
    RECONCILIATION_RUN: entry('业务对账', '只读一致性对账'),
    SALES_COMMISSION_PLAN: entry('前台销售分成计划', '前台销售员工的分成计划版本'),
    STUDENT: entry('学员', '学员档案'),
  },
  auditResult: {
    FAILURE: entry('失败', '操作未成功完成', 'red'),
    SUCCESS: entry('成功', '操作已成功完成', 'green'),
  },
  catalogStatus: {
    ACTIVE: entry('启用', '当前可用于新增业务', 'green'),
    INACTIVE: entry('停用', '保留历史记录但不可用于新增业务', 'default'),
  },
  commissionRole: {
    ASSISTANT: entry('助教', '以助教身份参与课销'),
    COACH: entry('上课教练', '课销的主教练'),
  },
  consumptionStatus: {
    ACTIVE: entry('有效', '课销当前有效', 'green'),
    CANCELLED: entry('已撤销', '课销已撤销并返还课时', 'default'),
    VOIDED_BY_PURCHASE: entry('购课联动作废', '关联购课作废导致课销失效', 'orange'),
  },
  courseType: {
    GROUP: entry('多人课', '同一批次可包含多个学员'),
    PRIVATE: entry('单人课', '单个学员的课销类型'),
  },
  employeeType: {
    COACH: entry('教练', '不可登录的授课人员'),
    FRONT_DESK: entry('前台', '处理学员、购课和课销'),
    VENUE_MANAGER: entry('馆长', '查看经营业务和审计信息'),
  },
  employmentStatus: {
    ACTIVE: entry('在职', '员工当前在职', 'green'),
    LEFT: entry('离职', '员工已离职且不能恢复', 'default'),
  },
  exportStatus: {
    FAILED: entry('失败', '任务处理失败，可按规则重试', 'red'),
    PROCESSING: entry('处理中', 'Worker 正在生成导出文件', 'processing'),
    SUCCESS: entry('成功', '导出文件已生成', 'green'),
    WAITING: entry('等待中', '任务正在等待处理', 'default'),
  },
  exportTaskType: {
    COMMISSION: entry('教练分成', '教练分成汇总与明细导出'),
    CONSUMPTION: entry('课销记录', '课销记录导出'),
  },
  fileStatus: {
    BOUND: entry('已绑定', '凭证已经绑定业务记录', 'success'),
    CLEAN: entry('处理完成', '图片检查完成', 'success'),
    CREATED: entry('等待上传', '可以上传图片', 'default'),
    EXPIRED: entry('已过期', '请重新选择图片', 'error'),
    INFECTED: entry('已阻止', '该图片无法使用', 'error'),
    PROCESSING: entry('处理中', '图片正在上传或处理', 'processing'),
    REJECTED: entry('校验未通过', '图片真实格式、大小或像素校验未通过', 'error'),
    SCANNING: entry('处理中', '正在检查图片', 'processing'),
    SCAN_FAILED: entry('处理失败', '图片检查失败，可以重试', 'warning'),
    UPLOADED: entry('已上传', '图片已上传并等待校验', 'processing'),
    VALIDATING: entry('校验中', '图片正在校验真实类型和像素', 'processing'),
    WAITING: entry('等待选择', '尚未选择凭证图片', 'default'),
  },
  notificationType: {
    EXPORT_FAILED: entry('导出失败', '导出任务失败通知', 'red'),
    EXPORT_SUCCESS: entry('导出成功', '导出文件生成通知', 'green'),
  },
  purchaseLedgerOperation: {
    CONSUMPTION_ADJUSTED: entry('课销调整', '修改课销引起的课时调整'),
    CONSUMPTION_CANCELLED: entry('撤销课销返还', '撤销课销后返还课时'),
    CONSUMPTION_DEDUCTED: entry('课销扣减', '有效课销扣减课时'),
    PURCHASE_ADJUSTED: entry('购课调整', '修改购课引起的课时调整'),
    PURCHASE_CREATED: entry('新增购课', '创建购课记录并增加课时'),
    PURCHASE_RESTORED: entry('恢复购课', '恢复购课记录及其课时'),
    PURCHASE_VOIDED: entry('作废购课', '作废购课记录及其课时'),
  },
  purchaseStatus: {
    ACTIVE: entry('有效', '购课记录当前有效', 'green'),
    VOIDED: entry('已作废', '购课记录已经作废', 'default'),
  },
  reconciliationStatus: {
    FAILED: entry('失败', '对账未能完成', 'red'),
    ISSUES_FOUND: entry('发现差异', '对账完成并发现业务差异', 'orange'),
    RUNNING: entry('执行中', '只读对账正在执行', 'processing'),
    SUCCESS: entry('一致', '对账完成且未发现差异', 'green'),
  },
  studentGender: {
    FEMALE: entry('女', '学员性别为女'),
    MALE: entry('男', '学员性别为男'),
    UNSPECIFIED: entry('未填写', '学员未填写性别'),
  },
  studentStatus: {
    ACTIVE: entry('正常', '学员当前可以继续办理业务', 'green'),
    LOST: entry('已流失', '学员当前标记为流失', 'default'),
  },
  userRole: {
    ADMIN: entry('管理员', '系统唯一管理员'),
    FRONT_DESK: entry('前台', '场馆前台员工'),
    VENUE_MANAGER: entry('馆长', '场馆管理人员'),
  },
} as const satisfies Record<BusinessCodeDomain, Readonly<Record<string, DictionaryEntry>>>;

const unknownLabels: Readonly<Record<BusinessCodeDomain, string>> = {
  auditAction: '未知操作',
  auditCheck: '未知检查项',
  auditModule: '未知模块',
  auditResult: '未知结果',
  catalogStatus: '未知状态',
  commissionRole: '未知人员类型',
  consumptionStatus: '未知状态',
  courseType: '未知课型',
  employeeType: '未知员工类型',
  employmentStatus: '未知状态',
  exportStatus: '未知状态',
  exportTaskType: '未知任务类型',
  fileStatus: '未知状态',
  notificationType: '未知通知类型',
  purchaseLedgerOperation: '未知课时变动',
  purchaseStatus: '未知状态',
  reconciliationStatus: '未知状态',
  studentGender: '未知性别',
  studentStatus: '未知状态',
  userRole: '未知角色',
};

export function businessCodeDisplay(
  domain: BusinessCodeDomain,
  value: unknown,
): BusinessCodeDisplay {
  const technicalValue = serializeTechnicalValue(value);
  const dictionary: Readonly<Record<string, DictionaryEntry>> = dictionaries[domain];
  const display = dictionary[technicalValue];
  if (display !== undefined) {
    return {
      color: display.color ?? 'default',
      description: display.description,
      known: true,
      label: display.label,
      technicalValue,
    };
  }
  return {
    color: 'default',
    description: '系统返回了尚未识别的业务代码，请联系管理员核对。',
    known: false,
    label: unknownLabels[domain],
    technicalValue,
  };
}

export function isKnownBusinessCode(domain: BusinessCodeDomain, value: unknown): boolean {
  return businessCodeDisplay(domain, value).known;
}

function entry(label: string, description: string, color?: string): DictionaryEntry {
  return color === undefined ? { description, label } : { color, description, label };
}

function serializeTechnicalValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return '';
}
